import React, { useCallback, useRef, useState } from 'react'
import openNewTab from '../../../lib/openNewTab'
import { wait } from '../../../lib/wait'
import modelApi from '../../../services/modelApi'
import Banner from '../../atoms/banner/Banner'
import Button from '../../atoms/button/Button'
import Text from '../../atoms/text/Text'
import InlineGroup from '../../atoms/inlinegroup/InlineGroup'
import VerticalGroup from '../../atoms/verticalgroup/VerticalGroup'
import CloseRoundModal from './CloseRoundModal'
import Icon from '../../atoms/icon/Icon'
import { usePollingModelConfiguration } from '../../../hooks/useModelConfiguration'

interface UIState {
  closeModalOpen: boolean
}
interface Props {
  handleCancel: () => void
  closeRound: () => Promise<void>
  isScheduledRound: boolean
  game: API.GameStatus
  round: number
}

const statuses = [
  'initialising' ,
  'initialised' ,
  'round-calculating' ,
  'round-calculated' ,
  'round-sculpting' ,
  'round-sculpted' ,
  'reporting-calculating' ,
  'reporting-calculated',
  'results-uploaded'
]

const Status = ({ modelConfig, desiredStatus, label, round }: { modelConfig: ModelAPI.ConfigurationResponse | null, desiredStatus: ModelAPI.ModelState, label: string, round: number }) => {
  const completed = modelConfig != null && modelConfig.currentRound === round && (statuses.indexOf(modelConfig.state) >= statuses.indexOf(desiredStatus))
  return (
    <InlineGroup spaceLeft={2}>
      <Icon noMargin type="status" colour={completed ? 'lightgreen' : 'grey1'} />
      <Text>{label}</Text>
    </InlineGroup>
  )
}

function CloseRoundScreen({ closeRound, handleCancel, isScheduledRound, game, round }: Props) {
  const [{ closeModalOpen }, setUIState] = useState<UIState>({
    closeModalOpen: false
  })
  const [inProgress, setInprogress] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const {
    data: modelConfig,
  } = usePollingModelConfiguration(game.gameId, 5000)
  const configRef = useRef<ModelAPI.ConfigurationResponse | null>()
  configRef.current = modelConfig

  const caculateResults = useCallback(async () => {
    try {
      setError(null)
      setInprogress(true)
      await modelApi.recalculateResults(game.gameId, round)
      await modelApi.sculptResults(game.gameId, round)
      await wait(5000)
      let iteration = 0
      while (true) {
        iteration++
        if (configRef.current?.state === 'round-sculpted') {
          break
        }
        if (iteration > 100) {
          throw new Error('Timed out sculpting')
        }
        await wait(2000)
      }
      await modelApi.recalculateReporting(game.gameId, round)
      await wait(5000)
      setInprogress(false)
    } catch (e) {
      setError(e.message)
      setInprogress(false)
    }
  }, [game.gameId, round])

  return (
    <>
      {closeModalOpen && <CloseRoundModal
        isOpen={closeModalOpen}
        onClose={() => setUIState(prevState => ({ ...prevState, closeModalOpen: false }))}
        onComplete={closeRound}
        game={game}
        round={round}
      />}
      {error && <Banner type='error' message={error} active />}
      {isScheduledRound && <InlineGroup spread block>
        <Button large onClick={handleCancel} light >Cancel</Button>
        <Button large onClick={() => setUIState({ closeModalOpen: true })} colour="blue" >Lock</Button>
      </InlineGroup>}
      {!isScheduledRound && <VerticalGroup spaceBetweenElements={2} wide>
        {round <= game.totalRounds && inProgress && modelConfig?.currentRound === round && <Text>{modelConfig?.progressMessage}</Text>}
        {round <= game.totalRounds && <Button block onClick={caculateResults} outline inProgress={inProgress}>Calculate Result</Button>}
        {round <= game.totalRounds && (<>
          <Status label='Round Calculated' modelConfig={modelConfig} round={round} desiredStatus='round-calculated' />
          <Status label='Round Sculpted' modelConfig={modelConfig} round={round} desiredStatus='round-sculpted' />
          <Status label='Reporting Generated' modelConfig={modelConfig} round={round} desiredStatus='reporting-calculated' />
        </>)}

        {round <= game.totalRounds && <Button block onClick={() => openNewTab(`${window.location.origin}/results/games/${game.gameId}/rounds/${round}`)} outline>View Results</Button>}
        {round <= game.totalRounds && <Button block onClick={() => setUIState({ closeModalOpen: true })} colour="blue" >Lock and proceed to the next round</Button>}
      </VerticalGroup>}
    </>
  )
}

export default CloseRoundScreen